import React from 'react';
import { X, CheckCircle, XCircle, SkipForward } from 'lucide-react';
import { EmailRecipient } from '../services/feedbackInstanceService';

interface EmailRecipientsDetailsProps {
  recipients: EmailRecipient[];
  onClose: () => void;
}

const statusConfig = {
  sent: { icon: CheckCircle, color: 'text-green-600 dark:text-green-400', label: 'Sent' },
  failed: { icon: XCircle, color: 'text-red-600 dark:text-red-400', label: 'Failed' },
  skipped: { icon: SkipForward, color: 'text-gray-500 dark:text-gray-400', label: 'Skipped' },
};

const EmailRecipientsDetails: React.FC<EmailRecipientsDetailsProps> = ({ recipients, onClose }) => {
  const sentCount = recipients.filter(r => r.status === 'sent').length;
  const failedCount = recipients.filter(r => r.status === 'failed').length;
  const skippedCount = recipients.filter(r => r.status === 'skipped').length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Email Recipients
            </h3>
            <div className="flex gap-3 mt-1 text-xs">
              {sentCount > 0 && (
                <span className="text-green-600 dark:text-green-400">{sentCount} sent</span>
              )}
              {failedCount > 0 && (
                <span className="text-red-600 dark:text-red-400">{failedCount} failed</span>
              )}
              {skippedCount > 0 && (
                <span className="text-gray-500 dark:text-gray-400">{skippedCount} skipped</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-4">
          <div className="space-y-1">
            {recipients.map((recipient, index) => {
              const config = statusConfig[recipient.status] || statusConfig.sent;
              const StatusIcon = config.icon;
              return (
                <div
                  key={index}
                  className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {recipient.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {recipient.email}
                    </p>
                  </div>
                  <div className={`flex items-center gap-1 text-xs ${config.color} ml-2`}>
                    <StatusIcon className="h-3.5 w-3.5" />
                    <span>{config.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailRecipientsDetails;
